import {BrowserRouter as Router, Routes, Route, Navigate} from "react-router-dom"
import { Suspense, lazy } from "react"
import { SavedPostsProvider } from "./hooks/useSavedPosts"
import { ConversationsProvider } from "./hooks/useConversations"
import { IntentsProvider, OnboardingGate } from "./hooks/useIntents"
import Header from "./components/Header";
import Loader from "./components/Loader";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import PrivateRoute from "./components/PrivateRoute";
import VerifiedRoute from "./components/VerifiedRoute";
import AdminRoute from "./components/AdminRoute";
import SiteFooter from "./components/SiteFooter";

// Pages are code-split: each loads as its own chunk on first navigation,
// so the initial bundle stays small.
const Home = lazy(() => import("./pages/Home"));
const Profile = lazy(() => import("./pages/Profile"));
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Feed = lazy(() => import("./pages/Feed"));
const Messages = lazy(() => import("./pages/Messages"));
const MessageThread = lazy(() => import("./pages/MessageThread"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const CreatePost = lazy(() => import("./pages/CreatePost"));
const EditPost = lazy(() => import("./pages/EditPost"));
const CreateUserName = lazy(() => import("./pages/createUserName"));
const PostDetails = lazy(() => import("./pages/PostDetails"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobDetails = lazy(() => import("./pages/JobDetails"));
const CreateJob = lazy(() => import("./pages/CreateJob"));
const Insights = lazy(() => import("./pages/Insights"));
const Welcome = lazy(() => import("./pages/Welcome"));
// Admin console. Its chunk is only requested after AdminRoute confirms the
// role claim, so strangers never download the admin code.
const YardOffice = lazy(() => import("./pages/admin/YardOffice"));
// Engineering portfolio, two variants sharing the same pages:
//   /portfolio       NDA-safe, the one sent to companies
//   /adams-projects  Adam's full version
// Standalone (no Jale header/footer/onboarding on either), and deliberately
// not linked from anywhere in the app — copy the links from /yard-office.
const PortfolioScope = lazy(() => import("./portfolio/PortfolioScope"));
const PortfolioHome = lazy(() => import("./portfolio/pages/PortfolioHome"));
const PortfolioAiCompanion = lazy(() => import("./portfolio/pages/AiCompanion"));
const PortfolioDelivery = lazy(() => import("./portfolio/pages/DeliveryIntelligence"));
const PortfolioFullStack = lazy(() => import("./portfolio/pages/FullStack"));

function App() {
  return (
    <IntentsProvider>
    <SavedPostsProvider>
    <ConversationsProvider>
     <Router>
        <Header/>
        <OnboardingGate/>
        <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<Home/>}/>

          <Route path="/profile" element={<VerifiedRoute />}>
            <Route path="/profile" element={<Profile/>}/>
          </Route>

          <Route path = "/create-username" element={<PrivateRoute />}>
          <Route path="/create-username" element={<CreateUserName />}/>
          </Route>

          {/* Logged in but not yet verified land here (login required, not verification) */}
          <Route path="/verify-email" element={<PrivateRoute />}>
            <Route path="/verify-email" element={<VerifyEmail />}/>
          </Route>

          <Route path="/sign-in" element={<SignIn/>}/>

          <Route path="/sign-up" element={<SignUp/>}/>

          <Route path="/forgot-password" element={<ForgotPassword/>}/>

          <Route path="/feed" element={<Feed/>}/>

          <Route path="/create-post" element={<VerifiedRoute />}>
            <Route path="/create-post" element={<CreatePost/>}/>
          </Route>

          <Route path="/edit-post" element={<VerifiedRoute />}>
            <Route path="/edit-post/:postId" element={<EditPost/>}/>
          </Route>

          <Route path="/category/:type/:postId" element={<PostDetails/>}/>

          <Route path="/jobs" element={<Jobs/>}/>
          <Route path="/jobs/:jobId" element={<JobDetails/>}/>

          <Route path="/create-job" element={<VerifiedRoute />}>
            <Route path="/create-job" element={<CreateJob/>}/>
          </Route>

          {/* "What brings you to Jale?" — after signup, and from Profile to change it */}
          <Route path="/welcome" element={<PrivateRoute />}>
            <Route path="/welcome" element={<Welcome/>}/>
          </Route>

          <Route path="/insights" element={<VerifiedRoute />}>
            <Route path="/insights" element={<Insights/>}/>
          </Route>

          <Route path="/u/:username" element={<PublicProfile/>}/>

          <Route path="/terms" element={<Terms/>}/>
          <Route path="/privacy" element={<Privacy/>}/>

          <Route path="/messages" element={<VerifiedRoute />}>
            <Route path="/messages" element={<Messages/>}/>
          </Route>

          <Route path="/messages/:conversationId" element={<VerifiedRoute />}>
            <Route path="/messages/:conversationId" element={<MessageThread/>}/>
          </Route>


          <Route path="/yard-office" element={<AdminRoute><YardOffice/></AdminRoute>}/>

          <Route path="/portfolio" element={<PortfolioScope variant="public"/>}>
            <Route index element={<PortfolioHome/>}/>
            <Route path="delivery-intelligence" element={<PortfolioDelivery/>}/>
            <Route path="full-stack" element={<PortfolioFullStack/>}/>
            <Route path="*" element={<Navigate to="/portfolio" replace/>}/>
          </Route>

          <Route path="/adams-projects" element={<PortfolioScope variant="full"/>}>
            <Route index element={<PortfolioHome/>}/>
            <Route path="ai-companion" element={<PortfolioAiCompanion/>}/>
            <Route path="delivery-intelligence" element={<PortfolioDelivery/>}/>
            <Route path="full-stack" element={<PortfolioFullStack/>}/>
            <Route path="*" element={<Navigate to="/adams-projects" replace/>}/>
          </Route>
        </Routes>
        </Suspense>
        <SiteFooter/>
      </Router>
      <ToastContainer
      position="bottom-center"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="dark"/>
    </ConversationsProvider>
    </SavedPostsProvider>
    </IntentsProvider>
  );
}

export default App;
